import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle, Loader2, ListChecks } from "lucide-react";
import {
  SwipeCard,
  type SwipeResult,
} from "@/components/inspections/swipe-card";
import {
  FailSheet,
  type FailSubmission,
} from "@/components/inspections/fail-sheet";

export const Route = createFileRoute(
  "/_authenticated/inspections/$inspectionId/swipe"
)({
  component: InspectionSwipePage,
});

interface InspectionItem {
  id: string;
  templateItemId: string;
  label: string | null;
  description: string | null;
  isRequired: boolean | null;
  sortOrder: number | null;
  result: string | null;
  notes: string | null;
  photoFileId: string | null;
}

interface InspectionDetail {
  id: string;
  status: string;
  date: string;
  notes: string | null;
  items: InspectionItem[];
}

interface PendingUpdate {
  templateItemId: string;
  result: SwipeResult;
  notes?: string;
  photoFileId?: string | null;
  severity?: string;
}

/**
 * Phase 4: swipe-based inspection completion UI.
 *
 * Loads the inspection + items via the existing detail endpoint and
 * walks the user card-by-card through every item. Each card can be
 * passed, failed (opens the FailSheet), or marked N/A. Progress is
 * persisted via the existing PATCH endpoint after each decision so the
 * user can drop off at any time without losing work.
 */
function InspectionSwipePage() {
  const { inspectionId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [cursor, setCursor] = useState(0);
  const [failingItem, setFailingItem] = useState<InspectionItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["inspection", inspectionId],
    queryFn: () =>
      apiGet<{ data: InspectionDetail }>(`/api/inspections/${inspectionId}`),
  });

  const inspection = data?.data;
  const items = useMemo(() => {
    return (inspection?.items ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [inspection]);

  const completedCount = items.filter((i) => i.result).length;
  const allAnswered = items.length > 0 && completedCount === items.length;

  const patchMutation = useMutation({
    mutationFn: (update: PendingUpdate) => {
      return apiPatch(`/api/inspections/${inspectionId}`, {
        items: [
          {
            templateItemId: update.templateItemId,
            result: update.result,
            notes: update.notes,
            photoFileId: update.photoFileId,
            severity: update.severity,
          },
        ],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["inspection", inspectionId],
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const completeMutation = useMutation({
    mutationFn: () =>
      apiPost<{ success: boolean; overallResult?: string }>(
        `/api/inspections/${inspectionId}/complete`
      ),
    onSuccess: (res) => {
      const result =
        (res as { overallResult?: string }).overallResult === "pass"
          ? "PASS"
          : "FAIL";
      toast.success(`Inspection completed: ${result}`);
      queryClient.invalidateQueries({
        queryKey: ["inspection", inspectionId],
      });
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      navigate({
        to: "/inspections/$inspectionId",
        params: { inspectionId },
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const advanceToNextUnanswered = (fromIndex: number) => {
    // Prefer the next unanswered item after fromIndex, else first unanswered
    for (let i = fromIndex + 1; i < items.length; i++) {
      if (!items[i].result) {
        setCursor(i);
        return;
      }
    }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].result) {
        setCursor(i);
        return;
      }
    }
    // All answered — stay where we are
    setCursor(Math.min(fromIndex, Math.max(0, items.length - 1)));
  };

  const handleResult = (result: SwipeResult) => {
    const item = items[cursor];
    if (!item) return;
    if (result === "fail") {
      setFailingItem(item);
      return;
    }
    patchMutation.mutate({
      templateItemId: item.templateItemId,
      result,
    });
    advanceToNextUnanswered(cursor);
  };

  const handleFailSubmit = (submission: FailSubmission) => {
    if (!failingItem) return;
    const item = failingItem;
    patchMutation.mutate({
      templateItemId: item.templateItemId,
      result: "fail",
      notes: submission.notes || undefined,
      photoFileId: submission.photoFileId,
      severity: submission.severity,
    });
    setFailingItem(null);
    const idx = items.findIndex((i) => i.id === item.id);
    advanceToNextUnanswered(idx);
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="text-center pt-12">
        <p className="text-muted-foreground">Inspection not found</p>
      </div>
    );
  }

  if (inspection.status === "completed") {
    return (
      <div className="max-w-lg mx-auto pt-8 text-center space-y-4">
        <CheckCircle className="h-10 w-10 text-green-600 mx-auto" />
        <p className="text-lg font-semibold">This inspection is complete.</p>
        <Button
          variant="outline"
          onClick={() =>
            navigate({
              to: "/inspections/$inspectionId",
              params: { inspectionId },
            })
          }
        >
          View details
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-lg mx-auto pt-8 text-center space-y-4">
        <p className="text-muted-foreground">
          This inspection has no check items.
        </p>
        <Button
          variant="outline"
          onClick={() =>
            navigate({
              to: "/inspections/$inspectionId",
              params: { inspectionId },
            })
          }
        >
          Back to details
        </Button>
      </div>
    );
  }

  const safeCursor = Math.min(cursor, items.length - 1);
  const current = items[safeCursor];
  const progress = items.length ? (completedCount / items.length) * 100 : 0;

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-20">
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={() =>
            navigate({
              to: "/inspections/$inspectionId",
              params: { inspectionId },
            })
          }
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-text-primary truncate">
            Inspection
          </h1>
          <p className="text-xs text-muted-foreground">
            {completedCount} of {items.length} answered
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() =>
            navigate({
              to: "/inspections/$inspectionId",
              params: { inspectionId },
            })
          }
        >
          <ListChecks className="h-4 w-4" />
          List view
        </Button>
      </div>

      <Progress value={progress} />

      <SwipeCard
        key={current.id}
        label={current.label || "Untitled item"}
        description={current.description}
        required={!!current.isRequired}
        index={safeCursor}
        total={items.length}
        onResult={handleResult}
      />

      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={safeCursor === 0}
          onClick={() => setCursor((c) => Math.max(0, c - 1))}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={safeCursor >= items.length - 1}
          onClick={() => setCursor((c) => Math.min(items.length - 1, c + 1))}
        >
          Skip
        </Button>
      </div>

      {allAnswered && (
        <Button
          className="w-full h-12 bg-paris-red hover:bg-paris-red-dark"
          disabled={completeMutation.isPending}
          onClick={() => completeMutation.mutate()}
        >
          {completeMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete inspection
            </>
          )}
        </Button>
      )}

      <FailSheet
        isOpen={!!failingItem}
        itemLabel={failingItem?.label || "Item"}
        initialNotes={failingItem?.notes ?? ""}
        onCancel={() => setFailingItem(null)}
        onSubmit={handleFailSubmit}
      />
    </div>
  );
}
